import { SITE_TUNING } from "./siteTuning";
import { clampPictionaryTranslationRechargeMs } from "../../shared/pictionaryTranslations";

const tuning = SITE_TUNING.pictionary.translations;
export type TranslationPhase = "ready" | "revealing" | "recharging";

/** Absolute deadlines survive file toggles, throttled tabs and ordinary page reloads. */
export class PictionaryTranslations {
    private count = 0;
    private firstAt: number | null = null;
    private complete = true;
    private key = "";
    private storageKey = "";
    private revealEndsAt = 0;
    private readyAt = 0;
    private rechargeMs: number = tuning.startRechargeMs;
    private rule: "guess-efficiency-v1" | "rank-v1" = "guess-efficiency-v1";
    private discoveryStorageKey = "";
    private discoveryRound = -1;
    private discoveryRoundStartedAt: number | null = null;
    private discoveryUsed = false;

    constructor(private readonly storage?: Pick<Storage, "getItem" | "setItem">) {}

    /** Discovery belongs to a player's game, independently of each case's recharge. */
    setGame(storageKey: string, roundNumber: number, roundStartedAt: number | null) {
        const discoveryKey = `discovery:${storageKey}`;
        if (this.discoveryStorageKey !== discoveryKey) {
            this.discoveryStorageKey = discoveryKey;
            this.discoveryRound = -1;
            this.discoveryRoundStartedAt = null;
            this.discoveryUsed = false;
            try {
                const saved = JSON.parse(this.storage?.getItem(discoveryKey) ?? "null");
                if (Number.isInteger(saved?.roundNumber) && saved.roundNumber >= 0) {
                    this.discoveryRound = saved.roundNumber;
                    this.discoveryRoundStartedAt = Number.isFinite(saved.roundStartedAt) ? saved.roundStartedAt : null;
                    this.discoveryUsed = saved.used === true;
                }
            } catch { /* Discovery also works without browser storage. */ }
        }
        if (roundNumber < this.discoveryRound ||
            (roundNumber === 1 && this.discoveryRound === 1 && roundStartedAt !== this.discoveryRoundStartedAt)) {
            this.discoveryUsed = false;
        }
        // Existing case history can restore first use after upgrading or a same-tab reload.
        this.discoveryUsed ||= this.count > 0 || this.revealEndsAt > 0;
        if (roundNumber !== this.discoveryRound || roundStartedAt !== this.discoveryRoundStartedAt) {
            this.discoveryRound = roundNumber;
            this.discoveryRoundStartedAt = roundStartedAt;
            this.saveDiscovery();
        }
    }

    private saveDiscovery() {
        try {
            this.storage?.setItem(this.discoveryStorageKey, JSON.stringify({
                roundNumber: this.discoveryRound, roundStartedAt: this.discoveryRoundStartedAt, used: this.discoveryUsed
            }));
        } catch { /* Keep the cue usable without browser storage. */ }
    }

    needsDiscovery(): boolean { return !this.discoveryUsed; }

    setCase(storageKey: string, key: string, rechargeMs: number, rule: "guess-efficiency-v1" | "rank-v1" = "guess-efficiency-v1") {
        if (this.key === key && this.storageKey === storageKey) return;
        this.key = key;
        this.storageKey = storageKey;
        this.count = 0;
        this.firstAt = null;
        this.complete = true;
        this.revealEndsAt = 0;
        this.readyAt = 0;
        this.rechargeMs = clampPictionaryTranslationRechargeMs(rechargeMs);
        this.rule = rule;
        try {
            const saved = JSON.parse(this.storage?.getItem(storageKey) ?? "null");
            if (saved?.key === key && Number.isFinite(saved.revealEndsAt) && Number.isFinite(saved.readyAt)
                && saved.readyAt >= saved.revealEndsAt) {
                this.revealEndsAt = saved.revealEndsAt;
                this.readyAt = saved.readyAt;
                this.complete = saved.complete === true && Number.isInteger(saved.count) && saved.count >= 0;
                this.count = Number.isInteger(saved.count) && saved.count >= 0 ? saved.count : 0;
                this.firstAt = this.count > 0 && Number.isFinite(saved.firstAt) ? saved.firstAt : null;
            }
        } catch { /* Storage may be unavailable in private or restricted browsers. */ }
    }

    reveal(now: number): boolean {
        if (now < this.readyAt) return false;
        this.discoveryUsed = true;
        if (this.discoveryStorageKey) this.saveDiscovery();
        this.count += 1;
        this.firstAt ??= now;
        this.revealEndsAt = now + tuning.revealMs;
        this.readyAt = this.revealEndsAt + this.rechargeMs;
        try {
            this.storage?.setItem(this.storageKey, JSON.stringify({
                key: this.key, revealEndsAt: this.revealEndsAt, readyAt: this.readyAt,
                count: this.count, firstAt: this.firstAt, complete: this.complete
            }));
        } catch { /* Keep the interaction available without browser storage. */ }
        return true;
    }

    getUsage() {
        return { count: this.count, firstAt: this.firstAt, complete: this.complete, revealMs: tuning.revealMs,
            rechargeMs: this.rechargeMs, rule: this.rule };
    }

    getState(now: number): { phase: TranslationPhase; progress: number; nextAt: number | null } {
        if (now < this.revealEndsAt) return {
            phase: "revealing", progress: Math.min(1, (this.revealEndsAt - now) / tuning.revealMs),
            nextAt: this.revealEndsAt
        };
        if (now < this.readyAt) return {
            phase: "recharging", progress: Math.max(0, (now - this.revealEndsAt) / (this.readyAt - this.revealEndsAt)),
            nextAt: this.readyAt
        };
        return { phase: "ready", progress: 1, nextAt: null };
    }
}
